import { useState, FormEvent } from 'react';

// TODO: 請前往 https://formspree.io 註冊免費帳號，並將下方的網址替換為分配給您的專屬表單 Endpoint
const FORM_URL = 'https://formspree.io/f/mvzvgvvo';

const categories = [
  '產品詢問／報價',
  '技術諮詢',
  '客製化需求',
  '代理合作洽談',
  '其他問題',
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('submitting');
    try {
      const formData = new FormData(form);
      const data = Object.fromEntries(formData.entries());
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center border border-gray-100 px-6">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1a73b5]/10 mb-4">
          <i className="ri-checkbox-circle-line text-[#1a73b5] text-4xl" />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">訊息已成功送出！</h3>
        <p className="text-sm text-gray-500 mb-6">感謝您的詢問，我們的專業顧問將於 1～2 個工作日內與您聯繫。</p>
        <button
          onClick={() => setStatus('idle')}
          className="inline-flex items-center gap-2 bg-[#1a73b5] text-white text-sm px-6 py-2.5 hover:bg-[#1558a0] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          再次詢問
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="border border-gray-100 p-6 md:p-8"
    >
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
        <div className="w-1 h-6 bg-[#1a73b5]" />
        <h3 className="text-base font-bold text-gray-800">填寫詢問表單</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {/* Name */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5 font-medium">
            姓名 <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="name"
            required
            placeholder="請輸入您的姓名"
            className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a73b5] transition-colors"
          />
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5 font-medium">公司名稱</label>
          <input
            type="text"
            name="company"
            placeholder="請輸入公司名稱（選填）"
            className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a73b5] transition-colors"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5 font-medium">
            聯絡電話 <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            name="tel"
            required
            placeholder="請輸入聯絡電話"
            className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a73b5] transition-colors"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5 font-medium">
            電子郵件 <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder="請輸入電子郵件"
            className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a73b5] transition-colors"
          />
        </div>
      </div>

      {/* Category */}
      <div className="mb-4">
        <label className="block text-sm text-gray-600 mb-1.5 font-medium">
          詢問類別 <span className="text-red-400">*</span>
        </label>
        <select
          name="category"
          required
          defaultValue=""
          className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a73b5] transition-colors bg-white cursor-pointer"
        >
          <option value="" disabled>請選擇詢問類別</option>
          {categories.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Message */}
      <div className="mb-6">
        <label className="block text-sm text-gray-600 mb-1.5 font-medium">
          詢問內容 <span className="text-red-400">*</span>
        </label>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="請詳細描述您的需求或問題，例如：產品型號、數量、使用環境等，以便我們提供最適合的解決方案。"
          onChange={e => setCharCount(e.target.value.length)}
          className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a73b5] transition-colors resize-none"
        />
        <div className="flex justify-between items-center mt-1">
          <p className={`text-xs ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
            {charCount > 500 ? '已超過字數限制，無法送出' : ''}
          </p>
          <p className={`text-xs ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
            {charCount} / 500
          </p>
        </div>
      </div>

      {/* Error */}
      {status === 'error' && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 px-4 py-3 mb-4">
          <i className="ri-error-warning-line text-red-400" />
          <p className="text-sm text-red-500">送出失敗，請稍後再試或直接來電聯繫。</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={status === 'submitting' || charCount > 500}
        className="w-full flex items-center justify-center gap-2 bg-[#1a73b5] text-white text-sm font-semibold py-3 hover:bg-[#1558a0] transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            送出中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            送出詢問
          </>
        )}
      </button>
      <p className="text-xs text-gray-400 text-center mt-3">
        標示 <span className="text-red-400">*</span> 為必填欄位，我們將於 1～2 個工作日內回覆
      </p>
    </form>
  );
}
